import { IPrice } from './../../interfaces/cart-details.interface';
import { Injectable } from '@angular/core';
import AbstractFormStateService from './abstract-form-state.service';
import { FormStateChange, FormStateChangeService, StateInputFields } from './form-state-change.service';
import { FormAddressModel } from './consumerDetailsModel';
import { DeliveryModeEnum } from '../../models/cartDetails/deliveryModeEnum.model';
import { CommonInfoService } from '../../cart-process/common-info.service';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../../external-interfaces/notification-provider.service';
import SlotService from '../../cart-process/slot.service';
import { PaymentModeTypeEnum } from '../../models/cartDetails/paymentModeType.model';


@Injectable()
export class DeliveryFormStateService extends AbstractFormStateService {

  public outputState = new DeliveryFormOutputState();

  protected inputState: any = {
    [StateInputFields.DELIVERY_MODE_NAME]: '',
    [StateInputFields.DELIVERY_OPTION_VALUE_NAME]: '',
    [StateInputFields.CONTRACT_ADDRESS_FORM_VALIDATE]: false,
    [StateInputFields.CONTRACT_ADDRESS_FORM_VALUES]: {},
    [StateInputFields.CONTRACT_ADDRESS_FORM_VALUES_STRING]: '',
    [StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_CHECKBOX]: null,
    [StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_FORM_VALIDATE]: false,
    [StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_FORM_VALUES]: {},
    [StateInputFields.INSTALLATION_OTHER_ADDRESS_CHECKBOX]: null,
    [StateInputFields.INSTALLATION_OTHER_ADDRESS_FORM_VALIDATE]: false,
    [StateInputFields.INSTALLATION_OTHER_ADDRESS_FORM_VALUES]: {},
    [StateInputFields.SELECTED_POS]: false,
    [StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_VALUES]: {},
    [StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_STRING]: '',
    [StateInputFields.PAYMENT_INVOICE_FORM_VALIDATE]: false,
    [StateInputFields.PAYMENT_INVOICE_ADDRESS_FORM_VALUES]: {},
    [StateInputFields.PAYMENT_MODE_VALUE_NAME]: '',
    [StateInputFields.IDENTITY_CONFIRMATION_TYPE]: '',
    [StateInputFields.POS_PAYMENT_MODES]: [],
    [StateInputFields.DELIVERY_COST]: ''
  };

  protected observedFields: any = [
    StateInputFields.DELIVERY_MODE_NAME,
    StateInputFields.DELIVERY_OPTION_VALUE_NAME,
    StateInputFields.CONTRACT_ADDRESS_FORM_VALIDATE,
    StateInputFields.CONTRACT_ADDRESS_FORM_VALUES,
    StateInputFields.CONTRACT_ADDRESS_FORM_VALUES_STRING,
    StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_CHECKBOX,
    StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_FORM_VALIDATE,
    StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_STRING,
    StateInputFields.INSTALLATION_OTHER_ADDRESS_CHECKBOX,
    StateInputFields.INSTALLATION_OTHER_ADDRESS_FORM_VALIDATE,
    StateInputFields.INSTALLATION_OTHER_ADDRESS_FORM_VALUES,
    StateInputFields.SELECTED_POS,
    StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_VALUES,
    StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_FORM_VALUES,
    StateInputFields.PAYMENT_INVOICE_FORM_VALIDATE,
    StateInputFields.PAYMENT_INVOICE_ADDRESS_FORM_VALUES,
    StateInputFields.PAYMENT_MODE_VALUE_NAME,
    StateInputFields.IDENTITY_CONFIRMATION_TYPE,
    StateInputFields.POS_PAYMENT_MODES,
    StateInputFields.DELIVERY_COST
  ];

  private hasM2mCardInCart = false;

  constructor(
    protected formStateChangeService: FormStateChangeService,
    private commonCartInfoService: CommonInfoService,
    private notificationProvider: NotificationProviderService,
  ) {
    super(formStateChangeService);
    this.subscribeToFormService();

    this.notificationProvider.subscribe(
      NotificationProviderTypeEnum.SLOTS_SERVICE,
      (slotService: SlotService): void => {
        if (slotService) {
          this.hasM2mCardInCart = slotService.hasM2mCardInCart();
        }
      }
    );
  }

  protected formatAddress(data: FormAddressModel): string {
    let address: string = [
      data.street ? 'ul. ' + data.street : '',
      [
        data.houseNumber || '',
        data.apartmentNumber ? '/' + data.apartmentNumber : '',
      ].join(''),
      data.postalCode || '',
      data.city || '',
    ].join(' ');

    address += data.province ? `, woj. ${data.province}` : '';

    return address;
  }

  protected formatAddressForGoogleMaps(data: FormAddressModel): string {
    const addressForGoogleMaps: string = [
      data.city + data.postalCode ? data.city + ', ' + data.postalCode : data.city
    ].join(' ');

    return addressForGoogleMaps;
  }

  protected handleStateChange(stateChange?: FormStateChange): void {
    // store input state
    if (stateChange) {
      this.inputState[stateChange.inputField] = stateChange.value;
    }

    /**
     * Common variables
     */

    const isB2c: boolean = this.commonCartInfoService.b2c;
    const isOtherSalesOffer: boolean = this.commonCartInfoService.isOtherSalesOffer;
    const isMigrationOffer: boolean = this.commonCartInfoService.isMigrationOffer;

    const isPos: boolean = this.inputState[StateInputFields.DELIVERY_MODE_NAME] === DeliveryModeEnum.POS;

    const deliveryOptionValueName
      = this.inputState[StateInputFields.DELIVERY_OPTION_VALUE_NAME];

    const paymentModeValueName
      = this.outputState.paymentModeValueName
      = this.inputState[StateInputFields.PAYMENT_MODE_VALUE_NAME];

    const showOtherCorrespondenceAddressVisible
      = this.inputState[StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_STRING];

    this.outputState.deliveryModeName
      = this.inputState[StateInputFields.DELIVERY_MODE_NAME];

    this.outputState.identityConfirmationType
      = this.inputState[StateInputFields.IDENTITY_CONFIRMATION_TYPE];

    this.outputState.deliveryCost
      = this.inputState[StateInputFields.DELIVERY_COST];

      /**
     * Variables for address selection visibility
     */

    this.outputState.isAddressOptionHidden = isPos || !this.outputState.deliveryModeName;
    this.outputState.isPosAddressHidden = !isPos;

    const isPosSelected: boolean
      = this.outputState.isPosSelected
      = this.inputState[StateInputFields.SELECTED_POS] as boolean;
    this.outputState.isMapModalButtonHidden = isPosSelected;
    this.outputState.isSelectedPosHidden = !isPosSelected;
    /**
     * Variables for delivery address same as contract address, variables delivery address for Google Maps.
     */

    const isContractAddressFormValid: boolean
      = this.inputState[StateInputFields.CONTRACT_ADDRESS_FORM_VALIDATE] as boolean;

    this.outputState.isDeliveryContractAddressControlDisabled = !isContractAddressFormValid;

    this.outputState.isContractAddressContentHidden
      = !isContractAddressFormValid
      || deliveryOptionValueName !== 'addressContract'
      || (isOtherSalesOffer && isB2c);

    if (isContractAddressFormValid) {
      this.outputState.valContractAddressFormValues
        = this.formatAddress(this.inputState[StateInputFields.CONTRACT_ADDRESS_FORM_VALUES] as FormAddressModel);

      this.outputState.valCorrespondenceAddressFormValuesForGoogleMaps
        = this.formatAddressForGoogleMaps(this.inputState[StateInputFields.CONTRACT_ADDRESS_FORM_VALUES] as FormAddressModel);
    }

    /**
     * Variables for delivery address same as correspondence address
     */

    this.outputState.isCorrespondenceAddressControlHidden =
      !this.inputState[StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_CHECKBOX]
      && showOtherCorrespondenceAddressVisible == '';

    this.outputState.isCorrespondenceAddressFormHidden =
      !this.inputState[StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_CHECKBOX];


    const isCorrespondenceAddressFormValid: boolean
      = (this.inputState[StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_FORM_VALIDATE] as boolean)
      || showOtherCorrespondenceAddressVisible !== '';

    this.outputState.isDeliveryCorrespondenceAddressControlDisabled = !isCorrespondenceAddressFormValid;

    this.outputState.isCorrespondenceAddressContentHidden
      = !isCorrespondenceAddressFormValid
      || deliveryOptionValueName !== 'addressCorrespondence'
      || this.outputState.isCorrespondenceAddressControlHidden;

    if (isCorrespondenceAddressFormValid) {
      this.outputState.valCorrespondenceAddressFormValues
        = this.formatAddress(this.inputState[StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_FORM_VALUES] as FormAddressModel);
    }

    if (isCorrespondenceAddressFormValid && showOtherCorrespondenceAddressVisible !== '') {
      this.outputState.valCorrespondenceAddressFormValues
        = this.formatAddress(this.inputState[StateInputFields.CORRESPONDENCE_OTHER_ADDRESS_VALUES] as FormAddressModel);
    }

    /**
     * Variables for delivery address same as installation address
     */

    this.outputState.isInstallationAddressControlHidden = !this.inputState[StateInputFields.INSTALLATION_OTHER_ADDRESS_CHECKBOX];
    this.outputState.showOtherInstallationAddressSection = this.hasM2mCardInCart && !isMigrationOffer && !isOtherSalesOffer;
    this.outputState.isInstallationAddressFormVisible = !!this.inputState[StateInputFields.INSTALLATION_OTHER_ADDRESS_CHECKBOX] && this.outputState.showOtherInstallationAddressSection;

    const isInstallationAddressFormValid: boolean
      = this.inputState[StateInputFields.INSTALLATION_OTHER_ADDRESS_FORM_VALIDATE] as boolean;

    this.outputState.isDeliveryInstallationAddressControlDisabled = !isInstallationAddressFormValid;

    this.outputState.isInstallationAddressContentHidden
      = !isInstallationAddressFormValid
      || deliveryOptionValueName !== 'addressInstallation'
      || this.outputState.isInstallationAddressControlHidden;

    if (isInstallationAddressFormValid) {
      this.outputState.valInstallationAddressFormValues
        = this.formatAddress(this.inputState[StateInputFields.INSTALLATION_OTHER_ADDRESS_FORM_VALUES] as FormAddressModel);
    }

    /**
     * Variables for other delivery address
     */

    const isDeliveryAddressFormValid: boolean
      = this.inputState[StateInputFields.PAYMENT_INVOICE_FORM_VALIDATE] as boolean;

    this.outputState.isDeliveryAddressContentHidden
      = !isDeliveryAddressFormValid
      || !isOtherSalesOffer;

    // true
    this.outputState.isOtherAddressFormHidden = deliveryOptionValueName !== 'addressOther' && !(isOtherSalesOffer && isB2c);
    this.outputState.deliveryAddressFormLabel = isOtherSalesOffer ? 'Adres dostawy' : 'Adres dostarczenia przesyłki';

    this.outputState.isDeliveryValid = !!(this.outputState.deliveryModeName && this.outputState.deliveryModeName !== DeliveryModeEnum.POS && (deliveryOptionValueName || isDeliveryAddressFormValid) || this.outputState.deliveryModeName === 'POS' && isPosSelected || this.outputState.deliveryModeName === DeliveryModeEnum.EMAIL);
    this.outputState.isPaymentValid = !!paymentModeValueName;

    /**
     * Other common variables
     */

    this.outputState.deliveryOptionValueName = this.inputState[StateInputFields.DELIVERY_OPTION_VALUE_NAME] = deliveryOptionValueName;

    /**
     * Correspondence address checkboxes
     */

    this.outputState.areCorrespondenceAddressRadiobuttonsVisible = !(isOtherSalesOffer && isB2c);
    this.emit();
  }

  public storeData() {
    return {
      save: (): void => {
        const data: string = JSON.stringify(this.inputState);

        sessionStorage.setItem('formStateData', JSON.stringify(data));
      },
      get: () => {
        let data = JSON.parse(sessionStorage.getItem('formStateData'));

        if (typeof data === 'string') {
          data = JSON.parse(data);
        }

        return data;
      },
      set: (): void => {
        const data = this.storeData().get();

        if (data) {
          this.inputState = data;

          this.handleStateChange();
        }
      },
      emit: (): void => {
        this.emit();
      }
    };
  }
}

export class DeliveryFormOutputState {
  public deliveryOptionValueName = '';
  public deliveryModeName = '';
  public paymentModeValueName = '';

  public isPosAddressHidden = false;
  public isAddressOptionHidden = false;
  public isContractAddressContentHidden = false;
  public isCorrespondenceAddressControlHidden = false;
  public isCorrespondenceAddressFormHidden = true;
  public isInstallationAddressControlHidden = false;
  public isCorrespondenceAddressContentHidden = false;
  public isInstallationAddressContentHidden = false;
  public isDeliveryAddressContentHidden = false;
  public isOtherAddressFormHidden = false;
  public showOtherInstallationAddressSection = false;
  public isInstallationAddressFormVisible = false;

  public isPosSelected = false;
  public isMapModalButtonHidden = false;
  public isSelectedPosHidden = false;

  public isDeliveryContractAddressControlDisabled = false;
  public isDeliveryCorrespondenceAddressControlDisabled = false;
  public isDeliveryInstallationAddressControlDisabled = false;

  public isDeliveryValid = false;
  public isPaymentValid = false;

  public valContractAddressFormValues = '';
  public valCorrespondenceAddressFormValues = '';
  public valInstallationAddressFormValues = '';
  public deliveryAddressFormLabel = '';
  public valCorrespondenceAddressFormValuesForGoogleMaps = '';

  public areCorrespondenceAddressRadiobuttonsVisible = false;

  public identityConfirmationType = '';

  public deliveryCost: IPrice;
  public requiresIdentityConfirmation: boolean;
}
